/**
 * Shared helpers for the AG Grid **server-side row model** datasources in this
 * directory (`Attribute/spaceLocalAttributesDataSource.ts`,
 * `Document/vaultDocumentsDataSource.ts`). They mirror each other's
 * block-index -> cursor walk deliberately; what lives here is the part where
 * duplicating the reasoning would be worse than sharing it.
 */
import { AgGrid } from "@jsq/ds/core";

/**
 * Settle a `getRows` whose response is being thrown away, rather than simply
 * returning from it.
 *
 * AG Grid's `RowNodeBlockLoader` counts a request as outstanding from
 * `registerLoads()` until the `success`/`fail` closure it handed the datasource
 * runs, so returning without calling either **leaks** one of the
 * `maxConcurrentDatasourceRequests` slots (default 2). Two consequences, both
 * reported as DV-673:
 *  - `isLoading()` is `activeBlockLoadsCount > 0`, and `PaginationComp` renders
 *    the last-row index as `?` whenever that is true — so the footer reads
 *    `1 to ? of N` from the first leak onward and never recovers.
 *  - `getAvailableLoadingCount()` is `maxConcurrentRequests - activeBlockLoadsCount`
 *    and `checkBlockToLoad` slices the pending blocks to it, so once both slots
 *    leak no block is ever fetched again and the row count freezes too.
 *
 * Calling `fail()` on an abandoned response is safe, and provably so rather than
 * by convention. The closure releases the slot **first and unconditionally**
 * (`rowNodeBlockLoader.loadComplete()`), and only then touches rows via
 * `cache.onLoadFailed(...)` — which early-returns unless `this.live`. `live` is
 * cleared by `destroyRowNodes()`, and every invalidation path reaches it:
 * `refreshAfterFilter` and `refreshServerSide({ purge: true })` both run
 * `LazyStore.refreshStore(true)` -> `destroyBean(this.cache)`. Since the closure
 * captured the *old* cache, an abandoned response finds
 * `LazyCache.live === false` and `onLoadFailed` no-ops. Net effect on every
 * path: the slot is released and no row is flipped to failed.
 *
 * That holds without any ordering guarantee from the caller, so pairing a
 * datasource's own `refresh()` with `refreshServerSide({ purge: true })` is not
 * load-bearing for this — it remains necessary only to reset the cursor map.
 */
export const abandonRequest = (params: AgGrid.IServerSideGetRowsParams): void => params.fail();
